// ── CHANGE ORDER MODAL ────────────────────────────────────────────────────
// File a Change Order (Option B) against one or more items past the Client
// Approved lock point. Bookkeeper picks fields to change, supplies new values
// and a reason, then submits — the CO is filed in 'pending' status and
// requires owner approval before it lands on the items.
//
// Shape:
//   open                bool
//   onClose             () => void
//   onSaved             (co) => void   — fires after successful file
//   initialItems        [item, ...]    — items to pre-populate
//   projectLabel        string         — required for the co_ref generator
//
// Wiring: parent imports useAppContext, passes context.fileChangeOrder via
// a thin onSubmit handler that builds the CO record and calls
// validateChangeOrder before firing.

import React, { useState, useMemo } from 'react';
import { useAppContext } from './AppContext';
import VendorPicker from './VendorPicker';
import {
  MATERIAL_FIELD_LABELS,
  QTY_TYPES,
  getMaterialFields,
  isBillingImpactingField,
  generateChangeOrderRef,
  validateChangeOrder,
} from '../utils';

// ── STYLES ─────────────────────────────────────────────────────────────────
var S = {
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 1000, fontFamily: "'DM Sans', sans-serif",
  },
  modal: {
    background: '#FFFFFF', borderRadius: 6, width: 720, maxWidth: '95vw',
    maxHeight: '90vh', display: 'flex', flexDirection: 'column',
    boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
  },
  header: {
    padding: '14px 18px', borderBottom: '0.5px solid #D8D7D2',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  },
  title: { fontSize: 14, fontWeight: 500, color: '#111111' },
  body: { padding: '16px 18px', overflowY: 'auto', flex: 1 },
  footer: {
    padding: '12px 18px', borderTop: '0.5px solid #D8D7D2',
    display: 'flex', justifyContent: 'flex-end', gap: 8, alignItems: 'center',
  },
  lbl: {
    fontSize: 10, fontWeight: 500, letterSpacing: '0.07em',
    textTransform: 'uppercase', color: '#888880', marginBottom: 6,
  },
  input: {
    width: '100%', padding: '7px 10px', fontSize: 12, fontFamily: 'inherit',
    border: '0.5px solid #D8D7D2', borderRadius: 4, background: '#FFFFFF',
    outline: 'none', boxSizing: 'border-box', color: '#111111',
  },
  textarea: {
    width: '100%', padding: '8px 10px', fontSize: 12, fontFamily: 'inherit',
    border: '0.5px solid #D8D7D2', borderRadius: 4, background: '#FFFFFF',
    outline: 'none', boxSizing: 'border-box', color: '#111111',
    minHeight: 64, resize: 'vertical',
  },
  itemBlock: {
    border: '0.5px solid #D8D7D2', borderRadius: 4, padding: '12px 14px',
    marginBottom: 12, background: '#FAFAF8',
  },
  itemHead: {
    fontSize: 12, fontWeight: 500, color: '#111111', marginBottom: 8,
  },
  fieldRow: {
    display: 'grid',
    gridTemplateColumns: '160px minmax(0,1fr) minmax(0,1fr) 28px',
    gap: 10, alignItems: 'center', marginBottom: 8,
  },
  fieldLabel: { fontSize: 11, color: '#111111' },
  beforeText: {
    fontSize: 11, color: '#555552',
    background: '#F4F4F0', borderRadius: 3, padding: '6px 8px',
    border: '0.5px solid #E0E0DC',
    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
  },
  removeBtn: {
    border: 'none', background: 'transparent', color: '#C8322A',
    cursor: 'pointer', fontSize: 14, lineHeight: 1, padding: 0,
  },
  pill: {
    display: 'inline-flex', alignItems: 'center', gap: 4,
    fontSize: 9, fontWeight: 500, letterSpacing: '0.05em',
    textTransform: 'uppercase', color: '#3730A3',
    background: '#E0E7FF', borderRadius: 10, padding: '2px 8px',
    marginLeft: 8,
  },
  addFieldBtn: {
    height: 26, padding: '0 10px', borderRadius: 4,
    border: '0.5px solid #D8D7D2', background: '#FFFFFF',
    cursor: 'pointer', fontSize: 11, fontWeight: 500, color: '#111111',
    fontFamily: 'inherit',
  },
  tbtnGhost: {
    height: 28, padding: '0 12px', borderRadius: 5,
    border: '0.5px solid #D8D7D2', background: '#FFFFFF',
    cursor: 'pointer', fontSize: 11, fontWeight: 500, color: '#111111',
    fontFamily: 'inherit',
  },
  tbtnPrimary: {
    height: 28, padding: '0 14px', borderRadius: 5,
    border: '0.5px solid #111111', background: '#111111',
    cursor: 'pointer', fontSize: 11, fontWeight: 500, color: '#FFFFFF',
    fontFamily: 'inherit',
  },
  errBox: {
    padding: '8px 12px', background: '#FCEAE8', border: '0.5px solid #C8322A',
    borderRadius: 4, color: '#A1271F', fontSize: 11, marginBottom: 12,
  },
};

// ── helpers ────────────────────────────────────────────────────────────────
function fmtBefore(v) {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'object') {
    try { return JSON.stringify(v); } catch (e) { return String(v); }
  }
  return String(v);
}

// Most material fields are simple scalars — render a text input. qtyType
// gets a select; markupPct + qty + netPrice are numeric.
function inputTypeFor(field) {
  if (field === 'qtyType') return 'select';
  if (field === 'netPrice' || field === 'markupPct' || field === 'qty') return 'number';
  return 'text';
}

// Use the canonical QTY_TYPES from utils.js — must match items.qty_type
// values exactly or downstream filters / joins silently exclude the row.

// ── COMPONENT ──────────────────────────────────────────────────────────────
export default function ChangeOrderModal(p) {
  var ctx = useAppContext();
  var changeOrders = ctx.changeOrders || [];
  var projects = ctx.projects || [];
  var userEmail = ctx.userEmail || '';
  var fileChangeOrder = ctx.fileChangeOrder;
  var vendors = ctx.vendors || [];
  var items = ctx.items || [];
  var purchaseOrders = ctx.purchaseOrders || [];
  var addVendorInline = ctx.addVendorInline;

  // Tracks per-item field selections + new values.
  // Shape: { [itemId]: { [field]: { after: <value> } } }
  var fcs = useState(function() {
    var seed = {};
    (p.initialItems || []).forEach(function(it) {
      if (it && it.id) seed[it.id] = {};
    });
    return seed;
  }());
  var fieldsByItem = fcs[0]; var setFieldsByItem = fcs[1];

  var rs = useState('');     var reason = rs[0];     var setReason = rs[1];
  var ss = useState(false);  var saving = ss[0];     var setSaving = ss[1];
  var es = useState('');     var err = es[0];        var setErr = es[1];

  // Project code lookup for ref generation
  var projectLabel = p.projectLabel || (p.initialItems && p.initialItems[0] && (p.initialItems[0].project || p.initialItems[0].projectLabel)) || '';
  var projectCode = useMemo(function() {
    var proj = projects.find(function(pr) { return pr.label === projectLabel; });
    return proj && proj.projectCode || '';
  }, [projects, projectLabel]);

  if (!p.open) return null;

  var materialFields = getMaterialFields();

  // Per-item helpers
  function addFieldToItem(itemId, field) {
    setFieldsByItem(function(prev) {
      var copy = Object.assign({}, prev);
      var current = Object.assign({}, copy[itemId] || {});
      // Seed `after` with the current item value so the user just edits it
      var srcItem = (p.initialItems || []).find(function(x) { return x.id === itemId; }) || {};
      var seedVal = srcItem[field];
      if (seedVal === null || seedVal === undefined) seedVal = '';
      current[field] = { after: typeof seedVal === 'object' ? JSON.stringify(seedVal) : String(seedVal) };
      copy[itemId] = current;
      return copy;
    });
  }
  function removeFieldFromItem(itemId, field) {
    setFieldsByItem(function(prev) {
      var copy = Object.assign({}, prev);
      var current = Object.assign({}, copy[itemId] || {});
      delete current[field];
      copy[itemId] = current;
      return copy;
    });
  }
  function updateAfter(itemId, field, value) {
    setFieldsByItem(function(prev) {
      var copy = Object.assign({}, prev);
      var current = Object.assign({}, copy[itemId] || {});
      current[field] = Object.assign({}, current[field] || {}, { after: value });
      copy[itemId] = current;
      return copy;
    });
  }

  function buildFieldChanges() {
    var changes = [];
    (p.initialItems || []).forEach(function(it) {
      if (!it || !it.id) return;
      var picked = fieldsByItem[it.id] || {};
      Object.keys(picked).forEach(function(field) {
        var beforeRaw = it[field];
        var before = (beforeRaw === null || beforeRaw === undefined) ? '' : (typeof beforeRaw === 'object' ? JSON.stringify(beforeRaw) : String(beforeRaw));
        var after = picked[field].after;
        // Numeric coercion for display + storage
        if (inputTypeFor(field) === 'number') {
          after = String(after);
        }
        // Snapshot the full pricing context at file time so Phase 5 QB
        // push can compute the dollar delta even if the item drifts
        // between filing and approval. Without this, a CO that changes
        // qty would compute the delta against current netPrice/markupPct
        // which may have ALSO been edited in the interim.
        var pricingSnapshot = {
          netPrice:    it.netPrice  != null ? String(it.netPrice)  : '',
          qty:         it.qty       != null ? String(it.qty)       : '',
          markupPct:   it.markupPct != null ? String(it.markupPct) : '',
          depositPct:  it.depositPct!= null ? String(it.depositPct): '',
        };
        changes.push({
          itemId:          it.id,
          itemNumber:      it.itemNumber || '',
          itemName:        it.productName || it.item || '',
          field:           field,
          fieldLabel:      MATERIAL_FIELD_LABELS[field] || field,
          before:          before,
          after:           after,
          impactsBilling:  isBillingImpactingField(field),
          pricingSnapshot: pricingSnapshot,
        });
      });
    });
    return changes;
  }

  async function handleSubmit() {
    setErr('');
    var fieldChanges = buildFieldChanges();
    var coRef = generateChangeOrderRef(projectCode, changeOrders);
    // Resolve the proposal this CO ties back to. If every item shares
    // the same proposalId, use it; otherwise omit (the schema column is
    // nullable). Phase 5 push uses this to locate the QB Estimate so it
    // can attach the credit memo / additional invoice draw.
    var proposalIds = (p.initialItems || [])
      .map(function(it) { return it && it.proposalId; })
      .filter(Boolean);
    var unanimousProposalId = proposalIds.length > 0
      && proposalIds.every(function(pid) { return pid === proposalIds[0]; })
      ? proposalIds[0]
      : '';
    var co = {
      coRef:        coRef,
      projectLabel: projectLabel,
      proposalId:   unanimousProposalId,
      reason:       reason,
      status:       'pending',
      filedBy:      userEmail,
      filedAt:      new Date().toISOString(),
      fieldChanges: fieldChanges,
    };
    var validationError = validateChangeOrder(co);
    if (validationError) { setErr(validationError); return; }
    if (!projectCode) { setErr('Could not resolve project code for "' + projectLabel + '". Set projectCode on the project record.'); return; }

    setSaving(true);
    try {
      var result = await fileChangeOrder(co);
      if (!result || !result.ok) {
        var msg = result && result.error;
        setErr(typeof msg === 'string' ? msg : (msg && msg.message) || 'Failed to file Change Order.');
        setSaving(false);
        return;
      }
      setSaving(false);
      if (typeof p.onSaved === 'function') p.onSaved(result.changeOrder);
      if (typeof p.onClose === 'function') p.onClose();
    } catch (e) {
      setSaving(false);
      setErr(e && e.message ? e.message : 'Unexpected error.');
    }
  }

  return (
    <div style={S.overlay} onClick={function(e) { if (e.target === e.currentTarget && !saving) p.onClose(); }}>
      <div style={S.modal}>
        <div style={S.header}>
          <span style={S.title}>File Change Order</span>
          <button
            onClick={function() { if (!saving && p.onClose) p.onClose(); }}
            style={{ background: 'transparent', border: 'none', fontSize: 18, color: '#888880', cursor: saving ? 'not-allowed' : 'pointer', padding: 0, lineHeight: 1 }}
            aria-label="Close">×</button>
        </div>

        <div style={S.body}>
          {err && <div style={S.errBox}>{err}</div>}

          <div style={{ marginBottom: 14 }}>
            <div style={S.lbl}>Reason <span style={{ color: '#C8322A' }}>*</span></div>
            <textarea
              value={reason}
              onChange={function(e) { setReason(e.target.value); }}
              placeholder="Why is this change needed? Include any relevant context for the audit trail."
              style={S.textarea} />
          </div>

          <div style={S.lbl}>Items + Fields to Change</div>
          {(p.initialItems || []).map(function(it) {
            if (!it || !it.id) return null;
            var picked = fieldsByItem[it.id] || {};
            var pickedKeys = Object.keys(picked);
            var available = materialFields.filter(function(f) { return pickedKeys.indexOf(f) === -1; });
            return (
              <div key={it.id} style={S.itemBlock}>
                <div style={S.itemHead}>
                  {it.itemNumber ? <span style={{ color: '#888880', marginRight: 6 }}>{it.itemNumber}</span> : null}
                  {it.productName || it.item || '(unnamed item)'}
                  {it.project ? <span style={{ color: '#888880', fontWeight: 400, marginLeft: 8 }}>· {it.project}</span> : null}
                </div>

                {pickedKeys.length === 0 ? (
                  <div style={{ fontSize: 11, color: '#888880', marginBottom: 8 }}>
                    No fields selected yet. Pick a field below to begin.
                  </div>
                ) : (
                  pickedKeys.map(function(field) {
                    var srcVal = it[field];
                    var ipt = inputTypeFor(field);
                    var afterVal = picked[field].after;
                    return (
                      <div key={field} style={S.fieldRow}>
                        <div style={S.fieldLabel}>
                          {MATERIAL_FIELD_LABELS[field] || field}
                          {isBillingImpactingField(field) && (
                            <span style={S.pill} title="Dollar delta will be billed on the next final invoice">$ Impact</span>
                          )}
                        </div>
                        <div>
                          <div style={S.lbl}>Current</div>
                          <div style={S.beforeText} title={fmtBefore(srcVal)}>{fmtBefore(srcVal)}</div>
                        </div>
                        <div>
                          <div style={S.lbl}>New</div>
                          {field === 'vendor' ? (
                            // CLAUDE.md "Vendor data integrity" — VendorPicker is the
                            // only sanctioned vendor input. Free-text was causing the
                            // CO modal to bypass the directory and not pull existing
                            // vendor names. Fix 2026-06-11.
                            <VendorPicker
                              value={afterVal}
                              onChange={function(v) { updateAfter(it.id, field, v); }}
                              vendors={vendors}
                              items={items}
                              purchaseOrders={purchaseOrders}
                              onAddVendor={addVendorInline} />
                          ) : ipt === 'select' ? (
                            <select
                              value={afterVal}
                              onChange={function(e) { updateAfter(it.id, field, e.target.value); }}
                              style={S.input}>
                              <option value="">—</option>
                              {QTY_TYPES.map(function(o) { return <option key={o} value={o}>{o}</option>; })}
                            </select>
                          ) : (
                            <input
                              type={ipt === 'number' ? 'number' : 'text'}
                              step={ipt === 'number' ? 'any' : undefined}
                              value={afterVal}
                              onChange={function(e) { updateAfter(it.id, field, e.target.value); }}
                              style={S.input} />
                          )}
                        </div>
                        <button
                          onClick={function() { removeFieldFromItem(it.id, field); }}
                          style={S.removeBtn}
                          title="Remove this field from the CO">×</button>
                      </div>
                    );
                  })
                )}

                {available.length > 0 && (
                  <div style={{ marginTop: 6 }}>
                    <select
                      value=""
                      onChange={function(e) { if (e.target.value) addFieldToItem(it.id, e.target.value); }}
                      style={Object.assign({}, S.addFieldBtn, { paddingRight: 24 })}>
                      <option value="">+ Add field…</option>
                      {available.map(function(f) {
                        return <option key={f} value={f}>{MATERIAL_FIELD_LABELS[f] || f}</option>;
                      })}
                    </select>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div style={S.footer}>
          {projectCode && (
            <span style={{ fontSize: 10, color: '#888880', marginRight: 'auto' }}>
              Will be filed as <strong style={{ color: '#111111' }}>{generateChangeOrderRef(projectCode, changeOrders)}</strong>
            </span>
          )}
          <button onClick={p.onClose} disabled={saving} style={S.tbtnGhost}>Cancel</button>
          <button onClick={handleSubmit} disabled={saving} style={Object.assign({}, S.tbtnPrimary, saving ? { opacity: 0.6, cursor: 'wait' } : {})}>
            {saving ? 'Filing…' : 'File Change Order'}
          </button>
        </div>
      </div>
    </div>
  );
}
